import { useMemo } from 'react';

import { WorkspaceMemberType } from '@air/api/types';

import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { canAddWorkspaceMembers, canCreateWorkspaceInviteLink } from '~/utils/permissions/workspacePermissions';
import { isTeamInviteNudgeWithinWindow, wasTeamInviteNudgeInviteTriggered } from '~/utils/teamInviteNudge';

const hasNoWorkspaceUsers = ({
  currentMemberAccountId,
  currentMemberEmail,
  members,
}: {
  currentMemberAccountId?: string | null;
  currentMemberEmail?: string | null;
  members: ReturnType<typeof useWorkspaceMembers>['members'];
}): boolean | undefined => {
  if (members && (currentMemberAccountId || currentMemberEmail)) {
    return members.allMembers.every((member) => {
      if (member.type !== WorkspaceMemberType.member) {
        return true;
      }

      return member.accountId === currentMemberAccountId || member.email === currentMemberEmail;
    });
  }

  return undefined;
};

export const useTeamInviteNudgeEligibility = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { data: subscription } = useSubscriptionContext();
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { members } = useWorkspaceMembers();

  const workspaceId = currentWorkspace?.id;
  const workspaceCreatedAt = currentWorkspace?.createdAt;

  const canInviteToWorkspace =
    canAddWorkspaceMembers(workspacePermissions) || canCreateWorkspaceInviteLink(workspacePermissions);

  const workspaceHasNoUsers = useMemo(
    () =>
      hasNoWorkspaceUsers({
        currentMemberAccountId: currentWorkspaceMember?.accountId,
        currentMemberEmail: currentWorkspaceMember?.email,
        members,
      }),
    [currentWorkspaceMember?.accountId, currentWorkspaceMember?.email, members],
  );

  const isTeamInviteNudgeEligibilityLoading =
    !!workspaceId && (!workspacePermissions || !subscription || typeof workspaceHasNoUsers !== 'boolean');

  const isTeamInviteNudgeEligible =
    !!workspaceId &&
    !!workspaceCreatedAt &&
    !isTeamInviteNudgeEligibilityLoading &&
    canInviteToWorkspace &&
    !!subscription?.isCreditModel &&
    !!workspaceHasNoUsers &&
    !wasTeamInviteNudgeInviteTriggered({ workspaceId }) &&
    isTeamInviteNudgeWithinWindow({ workspaceCreatedAt });

  return {
    isTeamInviteNudgeEligibilityLoading,
    isTeamInviteNudgeEligible,
    workspaceId,
    workspaceName: currentWorkspace?.name,
  };
};
